/**
 * Reduce a carrier name to a form that survives the usual differences in how
 * the same company gets typed into a tower: case, punctuation, "&" against
 * "and", and bracketed notes such as "(Non-Admitted)" or "($5M)".
 */
export const normaliseCarrierName = (name) => {
  if (typeof name !== "string") {
    return "";
  }
  return name
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/&/g, " and ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
};

/**
 * The names under which this company appears as a carrier in a tower: the
 * insurers in the `reinsurers` config (the paper the company writes on, by
 * `company`, or `name` where no company is given), plus any carrier
 * auto-completion marked `isOwnCarrier` for trading names that are not paper
 * companies. Returned normalised and without duplicates.
 */
export const ownCarrierNames = ({ reinsurers, carrierCompletions } = {}) => {
  const names = [
    ...(Array.isArray(reinsurers) ? reinsurers : []).map(
      (reinsurer) => reinsurer?.company || reinsurer?.name
    ),
    ...(Array.isArray(carrierCompletions) ? carrierCompletions : [])
      .filter((completion) => completion?.isOwnCarrier === true)
      .map((completion) => completion?.name),
  ]
    .map(normaliseCarrierName)
    .filter((name) => name !== "");
  return [...new Set(names)];
};

/**
 * Whether a carrier as typed in a tower share is one of ours. A whole-word
 * match is enough, so "Example Specialty Excess Ltd" or a share listing two
 * carriers in one box still counts, while "Northwind Specialty Insurance
 * Company" does not match "Northwind Insurance Company".
 */
export const isOwnCarrierName = (carrier, ownNames) => {
  const normalised = normaliseCarrierName(carrier);
  if (normalised === "" || !ownNames?.length) {
    return false;
  }
  const padded = ` ${normalised} `;
  return ownNames.some((own) => padded.includes(` ${own} `));
};

/** Whether any share of a tower layer is written by one of our carriers. */
export const isOwnTowerLayer = (layer, ownNames) =>
  (layer?.shares ?? []).some((share) =>
    isOwnCarrierName(share?.carrier, ownNames)
  );
